import { UseQueryResult, useQuery } from 'react-query'

import { isEmpty } from 'lodash'

import httpClient from 'services/http-client'

import { TEN_MINUTES_STALE_TIME } from 'constants/apis'
import { CategoryLevel } from 'constants/enums/product'

export const GET_PRODUCT_CATEGORIES = '/products/categories'

export type ProductCategory = {
  id: string
  name: string
  nameEn: string
  key: string
  parentId: string | null
  externalId?: string
  subCategories: ProductCategory[]
}

export type ProductCategoryMapByKey = Record<string, ProductCategory>

export type ProductCategoriesResult = {
  categories: ProductCategory[]
  categoryMapById: ProductCategoryMapByKey
  categoryMapByNameEn: ProductCategoryMapByKey
}

export const useProductCategories = (
  lang: string,
  isFlat = false,
  level: CategoryLevel = 'all'
): UseQueryResult<ProductCategoriesResult> =>
  useQuery({
    queryKey: [GET_PRODUCT_CATEGORIES, lang, isFlat, level],
    queryFn: async () => {
      const { data: categories } = await httpClient.get<ProductCategory[]>(GET_PRODUCT_CATEGORIES, {
        params: {
          lang,
          isFlat,
          level,
        },
      })
      const { categoryMapById, categoryMapByNameEn } = getCategoryMayById(categories)

      return {
        categories,
        categoryMapById,
        categoryMapByNameEn,
      }
    },
    staleTime: TEN_MINUTES_STALE_TIME,
  })

type ProductCategoryMap = {
  categoryMapById: ProductCategoryMapByKey
  categoryMapByNameEn: ProductCategoryMapByKey
}

const getCategoryMayById = (categories: ProductCategory[]): ProductCategoryMap => {
  let categoryMapById: ProductCategoryMapByKey = {}
  let categoryMapByNameEn: ProductCategoryMapByKey = {}

  categories.forEach(({ subCategories, ...restInfo }) => {
    if (restInfo.id) {
      categoryMapById[restInfo.id] = { ...restInfo, subCategories: [] }
    }

    if (restInfo.nameEn) {
      categoryMapByNameEn[restInfo.nameEn] = { ...restInfo, subCategories: [] }
    }

    if (!isEmpty(subCategories)) {
      const subCategoryMap = getCategoryMayById(subCategories)

      categoryMapById = { ...categoryMapById, ...subCategoryMap.categoryMapById }
      categoryMapByNameEn = { ...categoryMapByNameEn, ...subCategoryMap.categoryMapByNameEn }
    }
  })

  return { categoryMapById, categoryMapByNameEn }
}
